/**
 * Second iteration of a subscription edit dialog (replacing StepSubscriptionTermsDialog)
 * 
 * The purpose of the dialog is to allow the user to edit subscription settings on an SO.  This is
 * an alternative to a table row accordion approach.  We're going with the dialog so as to focus the
 * user on a single subscription, and avoid confusion of having more than one subscription edit process
 * open at a time.
 * 
 * This is set up as a container for the actual edit experience, in this case the ModelPicker, which
 * is used here as well as in AppAutosuggest.
 * 
 * Passed props:
 * - auth: authentication information
 * - open: boolean indicating whether the dialog is open or not
 * - customerAppId: the customerAppId of the subscription to edit
 * - inviteEmails: an array of emails as choices for inviting to manage the subscription
 * - defaultEmail: the default email to invite to the subscription
 * - onCancel: callback function to call when the user cancels the dialog
 * - onConfirm: callback function to call when the user confirms the dialog
 * 
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import AbEnvironment from '../auth/AbEnvironment';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Tooltip, Typography } from '@mui/material';

import ServerError from '../ui/ServerError';
import AppLogo from '../ui/AppLogo';
import ModelPicker from '../app/ModelPicker';

const abEnvironment = new AbEnvironment();
const StepSubscriptionEditDialogQuery = graphql`
query StepSubscriptionEditDialogQuery($customerAppId: UUID!) {
  customerAppTByCustomerAppId(customerAppId: $customerAppId) {
    customerAppId
    appId
    customerAccountId
    provisioningAccountId
    wholesaleDiscountPercentage
    appMarkupPercentage
    paymentOrder
    passThroughFees
    rAppMarkupPercentage
    rPaymentOrder
    rPassThroughFees
    soEntered
    appTByAppId {
      appName
      appSku
      appUrl
      appLogo
      appType
      description
      signupUrl
      showDomain
      showUsername
      appTByOemAppId {
        appId
      }
    }
    bindCardTByBcId {
      bcId
      extBcId
      bcStatus
    }
    accountTByCustomerAccountId {
      accountName
      accountId
      accountType
      contactEmail
      contactFirstName
      contactLastName
      contactEmail
    }
    flowTaskTsByCustomerAppId {
      totalCount
      nodes {
        flowTaskId
        taskType
        taskData
        title
        instruction
        linkName
        linkUrl
      }
    }    
  }
}
`;

class StepSubscriptionEditDialog extends Component {

  state = {
    wholesaleDiscountPercentage: null,
    appMarkupPercentage: null
  }

  render() {
    const { logError } = this.props.auth;

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={StepSubscriptionEditDialogQuery}
        variables={{customerAppId: this.props.customerAppId}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {
            let theSubscription = props.customerAppTByCustomerAppId;
            let theApp = theSubscription.appTByAppId;

            let taskType = 0;
            let instructionTitle = '';
            let instructionText = '';
            let linkName = '';
            let linkUrl = '';
            let askForEmail= true;
            let askForPassword= true;
            let askForUsername= false;
            let askForDomain = false;
            let askForPhone = false;

            let theInviteEmails = []; // This is the array of emails to invite to manage the subscription.

            if (theSubscription.paymentOrder === 4) {
              // This is a task/instruction case.
              let flowTasks = theSubscription.flowTaskTsByCustomerAppId.nodes;

              if (flowTasks.length > 0) {
                // Assuming one flowTask for now
                taskType         = flowTasks[0].taskType;
                instructionTitle = flowTasks[0].title;
                instructionText  = flowTasks[0].instruction;
                linkName         = flowTasks[0].linkName;
                linkUrl          = flowTasks[0].linkUrl;
  
                // Task data needs to be parsed
                let taskData = flowTasks[0].taskData;
                if (taskData != null) {
                  let tasksObj = taskData;
                  // let tasksObj = JSON.parse(taskData);
  
                  if (taskType === 0) {
                    // Auth task
                    if (tasksObj.email != null)    { askForEmail    = tasksObj.email; }
                    if (tasksObj.password != null) { askForPassword = tasksObj.password; }
                    if (tasksObj.username != null) { askForUsername = tasksObj.username; }
                    if (tasksObj.domain != null)   { askForDomain   = tasksObj.domain; }
                    if (tasksObj.phone != null)    { askForPhone    = tasksObj.phone; }
                  } else if (taskType === 1) {
                    // Invite tasks - so we should have email info.
                    if (tasksObj.inviteEmails != null) {
                      theInviteEmails = tasksObj.inviteEmails;
                    }
                  }
                }
              }

              // If the invite emails are empty, then we need to construct defaults from props.
              if (theInviteEmails.length === 0) {
                let accountEmails = [];
                if (this.props.inviteEmails != null && this.props.inviteEmails.length > 0) {
                    for (let i=0; i<this.props.inviteEmails.length; i++) {
                        let anEmail = this.props.inviteEmails[i];
                        accountEmails.push(anEmail);
                    }
                    accountEmails.sort();
                }
    
                // Now setup the array to use for the checkboxes
                for (let i=0; i<accountEmails.length; i++) {
                    let anEmail = accountEmails[i];
                    let anEmailObj = {email: anEmail, checked: false};
                    if (anEmail === this.props.defaultEmail) {
                        anEmailObj.checked = true;
                    }
                    theInviteEmails.push(anEmailObj);
                }
              }
            } else {
              // Subscription terms, but these don't need any special treatment.
            }

            let theAppModel = {
              appName:                      theApp.appName,
              paymentOrder:                 theSubscription.paymentOrder,
              rPaymentOrder:                theSubscription.rPaymentOrder,
              taskType:                     taskType,
              wholesaleDiscountPercentage:  theSubscription.wholesaleDiscountPercentage,
              appMarkupPercentage:          theSubscription.appMarkupPercentage,
              rAppMarkupPercentage:         theSubscription.rAppMarkupPercentage,
              instructionTitle:             instructionTitle,
              instructionText:              instructionText,
              linkName:                     linkName,
              linkUrl:                      linkUrl,             
              askForEmail:                  askForEmail,
              askForPassword:               askForPassword,
              askForUsername:               askForUsername,
              askForDomain:                 askForDomain,
              askForPhone:                  askForPhone,
              inviteEmails:                 theInviteEmails
            };

            let dialogTitle = 'Change subscription terms';
            if (theSubscription.paymentOrder === 4) {
              dialogTitle = 'Change subscription request';
            }

            /* PaperProps={{ sx: { mt: "50px", verticalAlign: "top" } }} */
            /* PaperProps={{ style: { position: 'absolute', right:60, top:30} }} */
            return (
              <Dialog
                disableEscapeKeyDown
                open={(!this.props.open ? false : this.props.open)}
                fullWidth={true}
                maxWidth={'md'}
                scroll='paper'
                PaperProps={{ style: { position: 'absolute', right:60, top:30} }}
              >
                <DialogTitle id="confirmation-dialog-title">{dialogTitle}</DialogTitle>
                <DialogContent>
                  <Grid container spacing={1} direction="row"  justifyContent="space-between" alignItems="center">
                    <Grid item xs={12} >
                      <Grid container alignItems="center" >
                        <Grid item >
                          <AppLogo logoPath={theSubscription.appLogo} appName={theSubscription.appName} imageHeight='24px'/>
                        </Grid>
                        <Grid item style={{marginLeft: '8px'}}>
                          <Typography>{theSubscription.appName}</Typography>
                        </Grid>
                        {/*
                        <Grid item > 
                          <Tooltip placement='right' title={currentTerms}>
                            <HelpOutline fontSize="small" style={{verticalAlign: "-8%", marginLeft: '4px'}}/>
                          </Tooltip>
                        </Grid>
                        */}
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <ModelPicker
                        auth={this.props.auth}
                        mode={"edit"}
                        appModel={theAppModel}
                        updateWdp={this.updateWdp}
                        updateAmp={this.updateAmp}
                        ref={(theModelPicker) => this.theModelPicker = theModelPicker}
                      />
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => this.handleCancel()} color="primary" variant="outlined">
                    {this.props.cancelBtn || 'Cancel'}
                  </Button>
                  <Button onClick={() => this.handleSave(props)} color="primary" variant="contained" disabled={this.isSaveSubscriptionDisabled(theSubscription.paymentOrder)}>
                    {this.props.okBtn || 'Save' }
                  </Button>
                </DialogActions>
              </Dialog>
            );
          }
          return (null); 
        }
      }
      />
    );
  }

  getErrorMessage(field, minValue, maxValue) {
    let errMesg = null;

    let value = Number(field); 
    if (value < minValue) {
        return 'Cannot be less than ' + minValue;
    }
    if (value > maxValue) {
        return 'Cannot be greater than ' + maxValue;
    }

    return errMesg;
  }

  areFieldsInError = () => {
    if (this.getErrorMessage(this.state.appMarkupPercentage, -100, 900) !== null) {
      return true;
    }
    if (this.getErrorMessage(this.state.wholesaleDiscountPercentage, 0, 100) !== null) {
      return true;
    }
    return false;
  }  
  
  updateWdp = (wdp) => {
    this.setState({wholesaleDiscountPercentage: wdp});
  }
  updateAmp = (amp) => {
    this.setState({appMarkupPercentage: amp});
  }

  isSaveSubscriptionDisabled(paymentOrder) {
    if (paymentOrder !== 4 && this.areFieldsInError() === true) {
        return true;
    }

    return false;
  }
    
  handleCancel = (event) => {
    if (this.props.onCancel) {
      this.props.onCancel(event);
    }
  };

  handleSave = (props) => {
    const { callPartnerApi } = this.props.auth;

    // When saving, we're either updating task/instructions, or changing payment terms.
    // In the first case (paymentOrder = 4), payment order won't be changing and terms are irrelevant.

    let theSubscription       = props.customerAppTByCustomerAppId;
    let paymentOrder          = theSubscription.paymentOrder;
    let customerAppId         = theSubscription.customerAppId;
    let usingAccountId        = theSubscription.customerAccountId;
    let provisioningAccountId = theSubscription.provisioningAccountId;

    // Information to save is on the model picker, so get the child state data
    let childState = this.theModelPicker.returnStateValuesToParent();

    if (paymentOrder === 5) {
      // Shouldn't be in here.  Just confirm.
      this.props.onConfirm();
    } else if (paymentOrder === 4) {
      // This is a task/instruction case.
      let flowTaskId = null;
      let taskType = null;
      let instructionTitle = null;
      let instructionText = null;
      let linkName = null;
      let linkUrl = null;
      let taskData = null;

      let flowTasks = theSubscription.flowTaskTsByCustomerAppId.nodes;

      if (flowTasks.length > 0) {
        flowTaskId       = flowTasks[0].flowTaskId;
        taskType         = flowTasks[0].taskType;
        instructionTitle = flowTasks[0].title;
        instructionText  = flowTasks[0].instruction;
        linkName         = flowTasks[0].linkName;
        linkUrl          = flowTasks[0].linkUrl;
        taskData         = flowTasks[0].taskData;
      }

      // Use state data if it exists
      if (childState.taskType != null)         { taskType         = childState.taskType; }
      if (childState.instructionTitle != null) { instructionTitle = childState.instructionTitle; }
      if (childState.instructionText != null)  { instructionText  = childState.instructionText; }
      if (childState.linkName != null)         { linkName         = childState.linkName; }
      if (childState.linkUrl != null)          { linkUrl          = childState.linkUrl; }
      if (childState.taskData != null)         { taskData         = childState.taskType; }

      // task data
      if (taskType === 0) {
          taskData = {
              email:    childState.askForEmail,
              password: childState.askForPassword,
              username: childState.askForUsername,
              domain:   childState.askForDomain,
              phone:    childState.askForPhone
          }
      } else if (taskType === 1) {
          taskData = {
              inviteEmails: childState.inviteEmails
          }

          if (linkUrl != null && linkUrl.length > 0 && (linkName == null || linkName.length === 0)) {
            linkName = 'Instructions';
          }
      }

      // Initiate promise chain.
      Promise.resolve()  
      .then(() => {
        let payload = {
          operation: "createOrUpdateFlowTask",
          params: {
            flowTaskId:          flowTaskId,
            taskType:            taskType,
            requestorAccountId:  provisioningAccountId,
            assignedAccountId:   usingAccountId,
            customerAppId:       customerAppId,
            taskData:            taskData,
            title:               instructionTitle,
            instruction:         instructionText,
            linkName:            linkName,
            linkUrl:             linkUrl
          }
        };

        return callPartnerApi('appbind/generic',
                              'POST',
                                payload);
      })
      .then((response) => {
        return response.json();
      })
      .then((jresp) => {
        //console.log(jresp); 
        this.props.onConfirm();

      }).catch((error) => {
        this.setState({
          saveStatus: 'error',
          saveMessage: 'We were unable to save your changes.  Please contact support.'
        });
      });
    } else {
      // This is a subscription terms case.
      if (childState.paymentOrder != null) {
        paymentOrder = childState.paymentOrder;
      }

      let wholesaleDiscountPercentage = theSubscription.wholesaleDiscountPercentage;
      if (childState.wholesaleDiscountPercentage != null) { 
        wholesaleDiscountPercentage = childState.wholesaleDiscountPercentage; 
      }

      let rAppMarkupPercentage = theSubscription.appMarkupPercentage;
      if (theSubscription.rAppMarkupPercentage != null) { 
        rAppMarkupPercentage = theSubscription.rAppMarkupPercentage; 
      }
      if (childState.appMarkupPercentage != null) { 
        rAppMarkupPercentage = childState.appMarkupPercentage;
      }
    
      // Initiate promise chain.
      Promise.resolve()  
      .then(() => {
        // Call the server side API
        let aPjwt = abEnvironment.getPjwt();

        let payload = {
          operation: "changeSubscriptionTerms",
          params: {
            pJwt:                        aPjwt,
            accountId:                   usingAccountId,
            pAccountId:                  provisioningAccountId,
            customerAppId:               customerAppId,
            alreadyApproved:             true,
            paymentOrder:                paymentOrder,
            wholesaleDiscountPercentage: wholesaleDiscountPercentage,
            appMarkupPercentage:         rAppMarkupPercentage
          }
        };

        return callPartnerApi('appbind/generic',
                              'POST',
                                payload);
      })
      .then((response) => {
        return response.json();
      })
      .then((jresp) => {
        //console.log(jresp); 
        this.props.onConfirm();
      }).catch((error) => {
        this.setState({
          saveStatus: 'error',
          saveMessage: 'There was a problem changing the subscription terms.  Please contact support.'
        });
      });
    }
  }
  
}
  
export default StepSubscriptionEditDialog;